import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, TextField, Typography } from '@material-ui/core';
import { Link, useLocation } from 'react-router-dom';
import { getBlogSessionToken } from '../../../Apis/touzenBlogApi';
import { deleteBlogComment, getBlogCommentLike, getBlogCommentLikeOperation, listBlogComments, saveBlogComment, setBlogCommentLike } from '../../../Apis/touzenBlogCommentApi';
import BlogLikeButton from './BlogLikeButton';
import BlogBodyText from './BlogBodyText';
import { PublicNameSettingsLink } from './PublicNameSettings';

const useStyles = makeStyles((theme) => ({
  section: { margin: theme.spacing(4, 0), padding: theme.spacing(2, 0), borderTop: `1px solid ${theme.palette.divider}`, overflowWrap: 'anywhere', textAlign: 'left' },
  heading: { fontSize: '1.125rem', fontWeight: 'bold', lineHeight: 1.5, marginBottom: theme.spacing(2) },
  text: { fontSize: '1rem', lineHeight: 1.8, color: theme.palette.text.primary, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  commentBody: { '& > p': { margin: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }, '& > p + p': { marginTop: theme.spacing(1) } },
  small: { fontSize: '0.875rem', lineHeight: 1.7, color: '#666' },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  comment: { padding: theme.spacing(2, 0), borderBottom: `1px solid ${theme.palette.divider}` },
  replies: { listStyle: 'none', margin: theme.spacing(0, 0, 0, 2), padding: theme.spacing(0, 0, 0, 2), borderLeft: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('xs')]: { marginLeft: 8, paddingLeft: 8 } },
  actions: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: theme.spacing(1, 0),
    '& .MuiButton-root': { minHeight: 44, fontSize: '0.875rem', fontFamily: theme.typography.fontFamily },
    '& button:focus-visible, & a:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } },
  button: { backgroundColor: 'rgb(192,192,192)', color: '#000', fontWeight: 'bold', borderRadius: '0.9em', padding: '8px 12px', boxShadow: '0 0 15px rgba(0,0,0,0.1)',
    '&:hover': { backgroundColor: '#bbb' } },
  field: { margin: theme.spacing(2, 0), '& .MuiInputBase-root': { fontSize: 16, fontFamily: theme.typography.fontFamily, borderRadius: 8, boxShadow: 'inset 0 0 15px rgba(0,0,0,0.1)' },
    '& .MuiInputLabel-root, & .MuiFormHelperText-root': { fontSize: 14, lineHeight: 1.7 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#999', borderWidth: '0.5px' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main } },
  gateField: { '& .MuiInputBase-root': { backgroundColor: theme.palette.action.hover }, '& textarea': { cursor: 'pointer' } },
}));
const uncertain = (error) => ![400, 401, 403, 404].includes(error?.status) && !['validation', 'forbidden', 'not_found', 'unauthenticated'].includes(error?.kind);
const errorMessage = (error, mutation = false) => {
  if (mutation && uncertain(error)) return '結果を確認できません。再送せず、コメントを再読み込みして確認してください。';
  return ({ validation: 'コメントは1〜2000文字で入力してください。', forbidden: 'この操作は許可されていません。投稿資格を確認してください。',
    unauthenticated: 'ログイン状態を確認してください。', session_changed: 'ログイン状態が変わりました。コメントを再読み込みしてください。',
    not_found: '記事またはコメントを利用できません。', timeout: '時間内に読み込めませんでした。' })[error?.kind] || 'コメントを読み込めませんでした。';
};

function CommentForm({ slug, comment = null, replyTo = null, options, disabled, onSaved, onCancel, onReload, verification = 0 }) {
  const classes = useStyles();
  const [body, setBody] = useState(comment?.body || '');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const controller = useRef(null);
  const sending = useRef(false);
  const editing = comment !== null;
  // A list read cannot prove a timed-out create failed; never unlock that POST.
  useEffect(() => { if (editing) { setUnconfirmed(false); setError(null); } }, [verification, editing]);
  useEffect(() => {
    if (controller.current?.signal.aborted) { sending.current = false; setBusy(false); setUnconfirmed(true); setError({ kind: 'session_changed' }); }
    return () => controller.current?.abort();
  }, [slug, options.expectedToken]);
  const submit = async (event) => {
    event.preventDefault();
    if (sending.current || disabled || unconfirmed) return;
    if (!body.trim() || Array.from(body).length > 2000) { setError({ kind: 'validation' }); return; }
    controller.current?.abort();
    const next = new AbortController(); controller.current = next;
    sending.current = true; setBusy(true); setError(null);
    try {
      const saved = await saveBlogComment(slug, comment?.id ?? null, body, replyTo?.id ?? null, { ...options, signal: next.signal });
      if (next.signal.aborted) return;
      setBody(''); onSaved(saved);
    } catch (failure) { if (!next.signal.aborted) { setError(failure); setUnconfirmed(uncertain(failure)); } }
    finally { if (!next.signal.aborted) { sending.current = false; controller.current = null; setBusy(false); } }
  };
  const reload = async () => { if (await onReload() && editing) { setError(null); setUnconfirmed(false); } };
  const label = comment ? 'コメントを編集' : replyTo ? '返信内容' : 'コメント内容';
  return <form noValidate onSubmit={submit} aria-label={label} aria-busy={busy}>
    {replyTo && <Typography className={classes.small}>{replyTo.author_display_name || '削除されたコメント'}への返信</Typography>}
    <TextField id={`comment-body-${comment ? 'edit' : replyTo ? 'reply' : 'new'}-${comment?.id || replyTo?.id || 'root'}`} label={label} className={classes.field} fullWidth multiline rows={3} minRows={3} variant="outlined" value={body}
      disabled={busy || disabled} error={error?.kind === 'validation'} onChange={(event) => { setBody(event.target.value); if (!unconfirmed) setError(null); }}
      helperText={error?.kind === 'validation' ? 'コメントは1〜2000文字で入力してください。' : `${Array.from(body).length} / 2000文字。投稿すると公開されます。`} />
    <div className={classes.actions}>
      <Button className={classes.button} type="submit" disabled={busy || disabled || unconfirmed}>{comment ? '変更を保存' : replyTo ? '返信を投稿' : 'コメントを投稿'}</Button>
      {onCancel && <Button type="button" onClick={onCancel} disabled={busy}>キャンセル</Button>}
      {(unconfirmed || (error && error.kind !== 'validation')) && <Button type="button" onClick={reload} disabled={busy || disabled}>コメントを再読み込み</Button>}
    </div>
    {busy && <Typography role="status" className={classes.small}>送信しています。</Typography>}
    {error && <Typography role="alert" className={classes.small}>{errorMessage(error, true)}</Typography>}
  </form>;
}

function CommentItem({ item, slug, options, authScope, disabled, onChange, onReply, onReload, verification }) {
  const classes = useStyles();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const sending = useRef(false);
  const controller = useRef(null);
  useEffect(() => {
    if (controller.current?.signal.aborted) { sending.current = false; setBusy(false); setUnconfirmed(true); setError({ kind: 'session_changed' }); }
    return () => controller.current?.abort();
  }, [slug, options.expectedToken]);
  useEffect(() => { setUnconfirmed(false); setError(null); setConfirming(false); }, [item, verification]);
  const adapter = useMemo(() => ({ get: (targetSlug, opts) => getBlogCommentLike(targetSlug, item.id, opts),
    set: (targetSlug, input, opts) => setBlogCommentLike(targetSlug, item.id, input, opts),
    status: (targetSlug, operation, opts) => getBlogCommentLikeOperation(targetSlug, item.id, operation, opts) }), [item.id]);
  const remove = async () => {
    if (sending.current || disabled || unconfirmed) return;
    sending.current = true; setBusy(true); setError(null);
    const next = new AbortController(); controller.current = next;
    try { const saved = await deleteBlogComment(slug, item.id, { ...options, signal: next.signal }); if (!next.signal.aborted) { onChange(saved); setConfirming(false); } }
    catch (failure) { if (!next.signal.aborted) { setError(failure); setUnconfirmed(uncertain(failure)); } }
    finally { if (!next.signal.aborted) { sending.current = false; controller.current = null; setBusy(false); } }
  };
  return <article className={classes.comment} aria-label={item.deleted ? '削除されたコメント' : `${item.author_display_name || '公開名未設定'}のコメント`}>
    {item.deleted ? <Typography className={classes.small}>{item.deletion_message}</Typography> : <>
      <Typography className={classes.small}>{item.author_display_name || '公開名未設定'} · <time dateTime={item.created_at}>{new Date(item.created_at).toLocaleString('ja-JP')}</time>{item.is_edited ? '（編集済み）' : ''}</Typography>
      {item.reply_to_id !== null && <Typography className={classes.small}>{item.reply_to_author_display_name || '削除されたコメント'}への返信</Typography>}
      {editing ? <CommentForm slug={slug} comment={item} options={options} disabled={disabled} verification={verification} onSaved={(saved) => { onChange(saved); setEditing(false); }} onCancel={() => setEditing(false)} onReload={onReload} />
        : <div className={`${classes.text} ${classes.commentBody}`}><BlogBodyText text={item.body} /></div>}
      <div className={classes.actions}>
        <BlogLikeButton key={item.id} slug={slug} api={adapter} storageScope={`${slug}:${item.id}`} isAuthenticated={options.authenticated} authScope={authScope} compact articleTitle="コメント" />
        {item.can_reply && <Button type="button" onClick={() => onReply(item)} disabled={disabled || busy}>返信</Button>}
        {item.can_edit && !editing && <Button type="button" onClick={() => setEditing(true)} disabled={disabled || busy}>編集</Button>}
        {item.can_delete && <Button type="button" onClick={() => setConfirming(true)} disabled={disabled || busy || unconfirmed}>削除</Button>}
      </div>
      {confirming && <div><Typography className={classes.small}>このコメントを削除しますか？本文は削除され、削除した記録が残ります。</Typography><div className={classes.actions}>
        <Button type="button" onClick={remove} disabled={busy || disabled || unconfirmed}>削除する</Button><Button type="button" onClick={() => setConfirming(false)} disabled={busy}>削除をやめる</Button>
      </div></div>}
      {error && <Typography role="alert" className={classes.small}>{errorMessage(error, true)}</Typography>}
      {unconfirmed && <div className={classes.actions}><Button type="button" onClick={onReload}>コメントを再読み込み</Button></div>}
    </>}
  </article>;
}

function Pager({ previous, next, busy, onPage, label }) {
  const classes = useStyles();
  return previous || next ? <nav aria-label={label} className={classes.actions}>
    <Button type="button" disabled={busy || !previous} onClick={() => onPage(previous)}>前へ</Button>
    <Button type="button" disabled={busy || !next} onClick={() => onPage(next)}>次へ</Button>
  </nav> : null;
}

function CommentThread({ initial, slug, options, authScope, disabled, onReload, verification, canComment }) {
  const classes = useStyles();
  const [root, setRoot] = useState(initial);
  const [replies, setReplies] = useState(initial.replies);
  const [replyToId, setReplyToId] = useState(null);
  const initialRef = useRef({ initial, canComment });
  const syncing = initialRef.current.initial !== initial || initialRef.current.canComment !== canComment;
  const [page, setPage] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const [paging, setPaging] = useState({ previous: null, next: initial.reply_next_page, busy: false, error: null, canComment });
  useEffect(() => {
    // useState already handles mount; only changed inputs may reset a paged thread.
    if (initialRef.current.initial === initial && initialRef.current.canComment === canComment) return;
    initialRef.current = { initial, canComment }; setRoot(initial); setReplies(initial.replies); setPage(1); setAttempt(0);
    setPaging({ previous: null, next: initial.reply_next_page, busy: false, error: null, canComment }); }, [initial, canComment]);
  useEffect(() => {
    if (page === 1 && attempt === 0) return undefined;
    const controller = new AbortController();
    setPaging((current) => ({ ...current, busy: true, error: null }));
    listBlogComments(slug, { ...options, page, thread: initial.id, signal: controller.signal }).then((data) => {
      if (!controller.signal.aborted) { setReplies(data.results); setRoot((current) => ({ ...current, reply_count: data.count }));
        setPaging({ previous: data.previous_page, next: data.next_page, busy: false, error: null, canComment: data.capabilities.can_comment && data.capabilities.is_authenticated }); }
    }).catch((error) => { if (!controller.signal.aborted) setPaging((current) => ({ ...current, busy: false, error })); });
    return () => controller.abort();
  }, [slug, initial.id, page, attempt, options]);
  const update = (saved) => {
    const latest = (item) => {
      const value = item.id === saved.id ? saved : item;
      return saved.deleted && value.reply_to_id === saved.id ? { ...value, reply_to_author_display_name: null } : value;
    };
    setRoot((current) => latest(current));
    // Keep the currently paged replies; a mutation's nested replies cover only page one.
    setReplies((current) => current.map(latest));
  };
  const replyTo = replyToId === null ? null : [root, ...replies].find((item) => item.id === replyToId);
  const canReply = canComment && paging.canComment && replyTo?.can_reply && !replyTo.deleted;
  const props = { slug, options, authScope, disabled: disabled || paging.busy || syncing, onChange: update, onReply: (item) => setReplyToId(item.id), onReload, verification };
  return <li>
    <CommentItem item={root} {...props} />
    {root.reply_count > 0 && <ol className={classes.replies} aria-label="返信">
      {replies.map((reply) => <li key={reply.id}><CommentItem item={reply} {...props} /></li>)}
    </ol>}
    {paging.busy && <Typography role="status" className={classes.small}>返信を読み込んでいます。</Typography>}
    {paging.error && <div role="alert"><Typography className={classes.small}>{errorMessage(paging.error)}</Typography><div className={classes.actions}><Button onClick={() => setAttempt((value) => value + 1)}>返信を再読み込み</Button></div></div>}
    <Pager previous={paging.previous} next={paging.next} busy={disabled || paging.busy} onPage={setPage} label="返信のページ送り" />
    {replyTo && <CommentForm key={replyTo.id} slug={slug} replyTo={replyTo} options={options} verification={verification} disabled={disabled || paging.busy || syncing || Boolean(paging.error) || !canReply} onCancel={() => setReplyToId(null)} onReload={onReload}
      onSaved={() => { setReplyToId(null); setPage(Math.max(1, Math.ceil((root.reply_count + 1) / 20))); setAttempt((value) => value + 1); }} />}
  </li>;
}

function CommentsContent({ slug, isAuthenticated, authScope }) {
  const classes = useStyles();
  const location = useLocation();
  const token = isAuthenticated ? getBlogSessionToken() : '';
  const options = useMemo(() => ({ authenticated: isAuthenticated, expectedToken: token }), [isAuthenticated, token]);
  const [page, setPage] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [dialog, setDialog] = useState(false);
  const [verification, setVerification] = useState(0);
  const [, refreshToken] = useState(0);
  const section = useRef(null);
  const refreshController = useRef(null);
  const current = useRef(options); current.current = options;
  const reload = async () => {
    refreshController.current?.abort();
    const controller = new AbortController(); refreshController.current = controller;
    setState((value) => ({ ...value, loading: true, error: null }));
    try {
      const data = await listBlogComments(slug, { ...options, page, signal: controller.signal });
      if (controller.signal.aborted || current.current !== options) return false;
      if (isAuthenticated && data.capabilities.can_comment) setDialog(false);
      setState({ data, loading: false, error: null }); setVerification((value) => value + 1); return true;
    } catch (error) { if (!controller.signal.aborted && current.current === options) setState((value) => ({ ...value, data: error?.kind === 'not_found' ? null : value.data, loading: false, error })); return false; }
  };
  useEffect(() => {
    const controller = new AbortController();
    setState((value) => ({ ...value, loading: true, error: null }));
    listBlogComments(slug, { ...options, page, signal: controller.signal }).then((data) => {
      if (!controller.signal.aborted && current.current === options) {
        setState({ data, loading: false, error: null });
        if (isAuthenticated && data.capabilities.can_comment) setDialog(false);
      }
    }).catch((error) => { if (!controller.signal.aborted && current.current === options) setState((value) => ({ ...value, data: error?.kind === 'not_found' ? null : value.data, loading: false, error })); });
    return () => { controller.abort(); refreshController.current?.abort(); };
  }, [slug, options, page, attempt, isAuthenticated]);
  useEffect(() => {
    const changed = (event) => { if (isAuthenticated && (event.key === 'accesstoken' || event.key === null)) refreshToken((value) => value + 1); };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [isAuthenticated]);
  const caps = state.data?.capabilities;
  const canPost = Boolean(isAuthenticated && caps?.is_authenticated && caps.can_comment);
  const blocked = Boolean(caps && !canPost);
  useEffect(() => {
    if (!blocked || state.loading || state.error) return undefined;
    const refreshBlocked = () => {
      // Presence only: a root refresh would unmount an editor on a later reply page.
      if (section.current?.querySelector('form')) return;
      setAttempt((value) => value + 1);
    };
    window.addEventListener('focus', refreshBlocked);
    return () => window.removeEventListener('focus', refreshBlocked);
  }, [blocked, state.loading, state.error]);
  const openGate = (event) => { event?.preventDefault(); if (!state.loading && !state.error) setDialog(true); };
  const gateKey = (event) => {
    if (event.key === 'Enter' || event.key === 'Process' || (Array.from(event.key).length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey)) openGate(event);
  };
  const needsName = Boolean(caps?.public_name_required);
  const dialogTitle = needsName ? '公開名の登録が必要です' : 'コメントの投稿は会員限定です';
  const domain = new URLSearchParams(location.search).get('domain') === 'touzen' ? '?domain=touzen' : '';
  return <section ref={section} className={classes.section} aria-labelledby="blog-comments-heading">
    <Typography component="h2" id="blog-comments-heading" className={classes.heading}>コメント</Typography>
    <Typography className={classes.small}>コメントの投稿・返信は現在有効な会員の方に限ります。公開名の登録が必要です。</Typography>
    {state.loading && <Typography role="status" className={classes.small}>コメントを読み込んでいます。</Typography>}
    {state.error && <div role="alert"><Typography className={classes.small}>{errorMessage(state.error)}</Typography><div className={classes.actions}><Button type="button" onClick={reload}>コメントを再読み込み</Button></div></div>}
    {state.data && <>
      {state.data.count === 0 && <Typography className={classes.small}>コメントはまだありません。</Typography>}
      <ol className={classes.list} aria-label="コメント一覧">{state.data.results.map((item) => <CommentThread key={item.id} initial={item} slug={slug} options={options} verification={verification}
        canComment={isAuthenticated && caps.is_authenticated && caps.can_comment} authScope={authScope} disabled={state.loading || Boolean(state.error)} onReload={reload} />)}</ol>
      <Pager previous={state.data.previous_page} next={state.data.next_page} busy={state.loading} onPage={setPage} label="コメントのページ送り" />
      {canPost ? <CommentForm slug={slug} options={options} verification={verification} disabled={state.loading || Boolean(state.error)} onReload={reload} onSaved={() => { setPage(Math.max(1, Math.ceil((state.data.count + 1) / 20))); setAttempt((value) => value + 1); }} />
        : <div>
          <TextField id="comment-eligibility-entry" className={`${classes.field} ${classes.gateField}`} label="コメント内容" placeholder="投稿条件を確認する" fullWidth multiline rows={3} variant="outlined" value=""
            InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} inputProps={{ 'aria-haspopup': 'dialog', 'aria-controls': 'comment-membership-dialog' }}
            disabled={state.loading || Boolean(state.error)} onClick={openGate} onKeyDown={gateKey} onChange={openGate} onPaste={openGate} onCompositionStart={openGate}
            helperText={needsName ? '投稿する前にMyPageで公開名を登録してください。' : '入力欄を押すと、投稿条件をご案内します。'} />
          {needsName && <div className={classes.actions}><PublicNameSettingsLink /></div>}
        </div>}
    </>}
    <Dialog id="comment-membership-dialog" open={dialog} onClose={() => setDialog(false)} aria-labelledby="comment-membership-title">
      <DialogTitle id="comment-membership-title" disableTypography className={classes.heading}>{dialogTitle}</DialogTitle>
      <DialogContent><Typography className={classes.text}>{needsName ? 'コメントを投稿する前に、MyPageで公開名を登録してください。' : caps?.reason === 'inactive_member' ? '休会中はコメントの閲覧のみご利用いただけます。' : '現在有効な会員の方が投稿できます。会員の方は会員アカウントでログインしてください。'}</Typography></DialogContent>
      <DialogActions className={classes.actions}>{needsName ? <PublicNameSettingsLink /> : <>
        {caps?.reason !== 'inactive_member' && <Button component={Link} role="link" to={`/login${domain}`}>ログイン</Button>}
        <Button component={Link} role="link" to={`/contact${domain}`}>お問い合わせ</Button>
      </>}<Button onClick={() => setDialog(false)}>閉じる</Button></DialogActions>
    </Dialog>
  </section>;
}

export default function BlogComments({ slug, isAuthenticated = false, authScope = '' }) {
  return <CommentsContent key={`${slug}:${isAuthenticated}:${authScope}`} slug={slug} isAuthenticated={isAuthenticated} authScope={authScope} />;
}
